import { VisuallyHidden } from '@radix-ui/react-visually-hidden';
import { useAnalyticsAvailability } from 'contexts/AnalyticsAvailabilityContext';
import { useReferrer } from 'contexts/ReferrerContext';
import { Container } from 'components/Container';
import { useTranslation } from 'hooks/useTranslation';
import { FunctionComponent, useEffect, useState } from 'react';
import styles from './SearchPage.module.scss';
import { Language, List, Service } from '@yleisradio/areena-types';
import { createSearchResultsPointer } from 'services/areena-api/pointer';
import logger from 'services/logger';
import { trackPage } from 'services/yleAnalyticsSdk';
import { useUILanguage } from 'hooks/useUILanguage';
import { List as ListComponent } from 'components/List';
import { ServiceSwitch } from 'components/ServiceSwitch';
import { SearchBar } from 'components/SearchBar/SearchBar';
import { SearchNotification } from '../Notifications/SearchNotification';

type Props = {
  activeService: Service;
  searchQuery: string;
  setActiveService: (service: Service) => void;
  setQuery: (query: string) => void;
};

export const SearchPage: FunctionComponent<Props> = ({
  activeService,
  searchQuery,
  setActiveService,
  setQuery,
}) => {
  const language = useUILanguage();
  const t = useTranslation();

  const { isAvailable: isAnalyticsAvailable } = useAnalyticsAvailability();
  const referrer = useReferrer();
  const [initialSearchQuery] = useState(searchQuery);
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState(searchQuery);

  useEffect(() => {
    if (!searchQuery) {
      return;
    }

    const timeout = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
    }, 500);

    return () => {
      clearTimeout(timeout);
    };
  }, [searchQuery]);

  useEffect(() => {
    if (isAnalyticsAvailable) {
      try {
        trackPage({
          countername: `search.${activeService}`,
          ...(referrer && { ns_referrer: referrer }),
          ...(initialSearchQuery && { yle_search_phrase: initialSearchQuery }),
        });
      } catch (e) {
        logger.error(e, 'Failed to track page view');
      }
    }
  }, [activeService, initialSearchQuery, isAnalyticsAvailable, referrer]);

  const formLabelId = 'search-page-heading';
  const inputId = 'search-page-input';

  return (
    <main id="maincontent">
      <Container>
        <form
          aria-labelledby={formLabelId}
          className={styles.form}
          onSubmit={(e) => e.preventDefault()}
        >
          <VisuallyHidden asChild>
            <h1>
              <label htmlFor={inputId} id={formLabelId}>
                {t('search')}
              </label>
            </h1>
          </VisuallyHidden>

          <div className={styles.inputWrapper}>
            <SearchBar
              searchQuery={searchQuery}
              setQuery={setQuery}
              inputId={inputId}
            />
          </div>

          <div className={styles.serviceSwitchWrapper}>
            <ServiceSwitch
              activeService={activeService}
              setActiveService={setActiveService}
            />
          </div>
        </form>
      </Container>

      {debouncedSearchQuery ? (
        <ListComponent
          index={0}
          list={createSearchResultsList(
            debouncedSearchQuery,
            language,
            activeService
          )}
          listKey="search-page-results"
          notificationComponent={SearchNotification}
        />
      ) : null}
    </main>
  );
};

function createSearchResultsList(
  query: string,
  language: Language,
  service: Service
): List {
  return {
    controls: [],
    filters: [],
    source: createSearchResultsPointer(query, language, service),
    style: {
      image: service === 'radio' ? '1:1' : '16:9',
      layout: 'grid',
      size: 'medium',
    },
    type: 'list',
  };
}
